import { useCallback } from "react";
import { validateNoAlphabets } from "../services/timeFormat";
import { scrollToTimePickerValue } from "../utils/timePickerUtils";
import { isAnyTimeInputFocused } from "../utils/trackerHelpers";

/**
 * Custom hook for time input handlers (onChange, onClick, onBlur, onFocus)
 * Reduces code duplication across time input fields
 */
export const useTimeInputHandlers = ({
  pickerType, // 'start24', 'end24', 'start12', 'end12'
  value,
  setValue,
  inputRef,
  timePickerRefs, // Dropdown container refs (for scrolling)
  allInputRefs, // All input field refs (for focus checking) - optional
  timePickerOpen,
  setTimePickerOpen,
  setTimePickerSelection,
  isSettingFromDropdownRef,
  justOpenedDropdownRef,
  setSplitted,
  setSplittedDate,
  setIsTimeInputActive,
  manualChangeHandler, // Function to call on blur for validation
  is24Hour = true,
}) => {
  const handleChange = useCallback(
    (e) => {
      const inputValue = e.target.value;

      // For 24-hour format, validate no alphabets
      if (is24Hour && !validateNoAlphabets(inputValue)) {
        return;
      }

      setValue(inputValue);

      // Only scroll if value is being typed, not set from dropdown
      if (!isSettingFromDropdownRef.current) {
        // Ensure dropdown is open when typing (needed for scrolling to work)
        const wasClosed = !timePickerOpen[pickerType];
        if (wasClosed) {
          setTimePickerOpen((prev) => ({
            ...prev,
            start24: false,
            end24: false,
            start12: false,
            end12: false,
            [pickerType]: true,
          }));
        }

        // Clear splitted state when user manually types
        setSplitted(false);
        setSplittedDate([]);

        // Update selection state and scroll to value
        const timeMatch = is24Hour
          ? inputValue.match(/(\d{1,2})[:\s]*(\d{0,2})/)
          : inputValue.match(/(\d{1,2})[:\s]*(\d{0,2})\s*(AM|PM)?/i);

        if (timeMatch) {
          const [, hour, minute, ampm] = timeMatch;
          const hourValue = hour ? hour.padStart(2, "0") : null;
          const minuteValue = minute ? minute.padStart(2, "0") : null;
          const ampmValue = ampm ? ampm.toUpperCase() : null;

          setTimePickerSelection((prev) => ({
            ...prev,
            [pickerType]: {
              hour: hourValue || (prev[pickerType]?.hour || (is24Hour ? "00" : "01")),
              minute: minuteValue || (prev[pickerType]?.minute || "00"),
              ampm: is24Hour ? null : (ampmValue || prev[pickerType]?.ampm || "AM"),
            },
          }));

          // Scroll to the selected value - use setTimeout to ensure dropdown is open and rendered
          if (hourValue || minuteValue || ampmValue) {
            // Use requestAnimationFrame + setTimeout to ensure DOM is ready
            requestAnimationFrame(() => {
              setTimeout(() => {
                scrollToTimePickerValue(pickerType, hourValue, minuteValue, ampmValue, {
                  timePickerRefs,
                  timePickerOpen: { ...timePickerOpen, [pickerType]: true }, // Pass updated state
                  isSettingFromDropdownRef,
                });
              }, wasClosed ? 150 : 50); // Longer delay if we just opened the dropdown
            });
          }
        }
      }
    },
    [
      pickerType,
      setValue,
      is24Hour,
      isSettingFromDropdownRef,
      setSplitted,
      setSplittedDate,
      setTimePickerSelection,
      timePickerRefs,
      timePickerOpen,
      setTimePickerOpen,
    ]
  );

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      // Always open the dropdown when clicking on input (don't toggle)
      justOpenedDropdownRef.current[pickerType] = true;

      // Close other pickers and open this one
      const newState = {
        start24: false,
        end24: false,
        start12: false,
        end12: false,
        [pickerType]: true,
      };
      setTimePickerOpen((prev) => ({ ...prev, ...newState }));

      // Clear the flag after a short delay to allow normal blur behavior
      setTimeout(() => {
        justOpenedDropdownRef.current[pickerType] = false;
      }, 300);
    },
    [pickerType, setTimePickerOpen, justOpenedDropdownRef]
  );

  const handleBlur = useCallback(
    (e) => {
      // Don't close if we just opened the dropdown (prevents immediate closing)
      if (justOpenedDropdownRef.current[pickerType]) {
        return;
      }

      // Don't close if clicking on the time picker dropdown
      const relatedTarget = e.relatedTarget || document.activeElement;
      const pickerRef = timePickerRefs[pickerType]?.current;
      if (pickerRef?.contains(relatedTarget)) {
        // Keep focus on input if clicking inside dropdown
        setTimeout(() => {
          if (inputRef.current && !pickerRef?.contains(document.activeElement)) {
            inputRef.current.focus();
          }
        }, 0);
        return;
      }

      setTimeout(() => {
        // If any time input has focus, keep dropdown open
        // Use allInputRefs if provided, otherwise fallback to checking timePickerRefs
        if (allInputRefs) {
          if (isAnyTimeInputFocused(allInputRefs)) {
            return;
          }
        } else {
          // Fallback: try to check if any input has focus
          // Note: This is a fallback and may not work perfectly without proper input refs
          const activeElement = document.activeElement;
          if (activeElement && activeElement.tagName === 'INPUT' && activeElement.type === 'text') {
            return;
          }
        }

        // Validate and format on blur
        if (value && manualChangeHandler) {
          manualChangeHandler(value);
        }
        setIsTimeInputActive(false);

        // Close dropdown on blur if not clicking inside it and not focusing another time input
        if (!pickerRef?.contains(document.activeElement)) {
          setTimePickerOpen((prev) => ({ ...prev, [pickerType]: false }));
        }
      }, 200);
    },
    [
      pickerType,
      value,
      inputRef,
      timePickerRefs,
      justOpenedDropdownRef,
      setIsTimeInputActive,
      manualChangeHandler,
      setTimePickerOpen,
    ]
  );

  const handleFocus = useCallback(() => {
    setIsTimeInputActive(true);
    // Close other pickers and open this one
    const newState = {
      start24: false,
      end24: false,
      start12: false,
      end12: false,
      [pickerType]: true,
    };
    setTimePickerOpen((prev) => ({ ...prev, ...newState }));
  }, [pickerType, setIsTimeInputActive, setTimePickerOpen]);

  return {
    onChange: handleChange,
    onClick: handleClick,
    onBlur: handleBlur,
    onFocus: handleFocus,
  };
};
